(() => {
  if (window.ArchiveOwnership) return;

  const STATE_KEY = 'twaArchiveOwnershipConsent.v1';
  const PROFILE_KEY = 'twaArchiveProfile.v1';
  const WATCHLIST_KEY = 'archiveWatchlist';
  const PENDING_ACTION_KEY = 'twaArchivePendingAction.v1';
  const PENDING_ACTION_MAX_AGE = 30 * 60 * 1000;
  const customerId = normaliseCustomerId(window.archiveWatchlistConfig?.customerId);
  const isAuthenticated = Boolean(window.archiveWatchlistConfig?.isAuthenticated && customerId);
  let activeModal = null;
  let resolveReady;
  let resolveProfileHydration;

  const ready = new Promise(resolve => {
    resolveReady = resolve;
  });

  const profileHydrationReady = new Promise(resolve => {
    resolveProfileHydration = resolve;
  });

  function finish(result) {
    resolveReady(result);
    window.archiveResolveOwnership?.(result);
    delete window.archiveResolveOwnership;
  }

  function normaliseCustomerId(value) {
    const normalised = value === null || value === undefined ? '' : String(value).trim();
    return /^\d+$/.test(normalised) ? normalised : null;
  }

  function readJson(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (error) {
      console.warn(`Unable to read ${key}.`, error);
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Unable to write ${key}.`, error);
    }
  }

  function readSessionJson(key, fallback) {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (error) {
      console.warn(`Unable to read ${key}.`, error);
      return fallback;
    }
  }

  function clearPendingAction() {
    try {
      sessionStorage.removeItem(PENDING_ACTION_KEY);
    } catch (error) {
      console.warn(`Unable to clear ${PENDING_ACTION_KEY}.`, error);
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has('archiveHandoff')) {
      url.searchParams.delete('archiveHandoff');
      history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  function readState() {
    const state = readJson(STATE_KEY, {});
    return state && typeof state === 'object'
      ? { version: 1, anonymous: state.anonymous || null, customers: state.customers || {} }
      : { version: 1, anonymous: null, customers: {} };
  }

  function writeState(state) {
    writeJson(STATE_KEY, state);
  }

  function validWatchlist(value) {
    return Array.isArray(value) ? value : [];
  }

  function validProfile(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }

  function hasSelectedDistilleries(distilleries) {
    return distilleries && typeof distilleries === 'object' &&
      Object.values(distilleries).some(value => Array.isArray(value) && value.length > 0);
  }

  function hasMeaningfulProfile(profile, personaInteracted = false) {
    const storedProfile = validProfile(profile);
    const archiveProfile = storedProfile.archiveProfile;
    if (!archiveProfile || typeof archiveProfile !== 'object') return false;
    if (personaInteracted) return true;
    const persona = archiveProfile.persona || storedProfile.currentPersona;
    if (persona && String(persona).toLowerCase() !== 'collector') return true;
    if (hasSelectedDistilleries(archiveProfile.distilleries)) return true;

    return Object.entries(archiveProfile).some(([key, value]) =>
      key !== 'persona' && key !== 'distilleries' && Array.isArray(value) && value.length > 0
    );
  }

  function canonicalize(value) {
    if (Array.isArray(value)) return value.map(canonicalize);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce((result, key) => {
        result[key] = canonicalize(value[key]);
        return result;
      }, {});
    }
    return value;
  }

  function fingerprint(data) {
    const input = JSON.stringify(canonicalize(data));
    let hash = 2166136261;
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `v1-${(hash >>> 0).toString(16)}`;
  }

  function anonymousFingerprint(anonymous) {
    return fingerprint({
      profile: validProfile(anonymous?.profile),
      watchlist: validWatchlist(anonymous?.watchlist),
      personaInteracted: anonymous?.personaInteracted === true
    });
  }

  function pendingArchiveAction(anonymous) {
    const pending = readSessionJson(PENDING_ACTION_KEY, null);
    const nonce = new URLSearchParams(window.location.search).get('archiveHandoff');
    const valid = pending?.version === 1 &&
      (pending.action === 'save-profile' || pending.action === 'save-watchlist') &&
      pending.returnPath === window.location.pathname &&
      pending.nonce && pending.nonce === nonce &&
      Number.isFinite(pending.createdAt) &&
      Date.now() - pending.createdAt >= 0 &&
      Date.now() - pending.createdAt <= PENDING_ACTION_MAX_AGE &&
      pending.anonymousFingerprint === anonymous?.fingerprint;

    if (!valid) {
      if (pending || nonce) clearPendingAction();
      return null;
    }

    return pending;
  }

  function meaningfulAnonymous(anonymous) {
    return Boolean(
      anonymous &&
      (validWatchlist(anonymous.watchlist).length > 0 ||
        hasMeaningfulProfile(anonymous.profile, anonymous.personaInteracted === true))
    );
  }

  function snapshotCurrentAnonymous(existing = null) {
    const profile = validProfile(readJson(PROFILE_KEY, {}));
    const watchlist = validWatchlist(readJson(WATCHLIST_KEY, []));
    const anonymous = {
      profile,
      watchlist,
      personaInteracted: existing?.personaInteracted === true
    };
    anonymous.fingerprint = anonymousFingerprint(anonymous);
    return anonymous;
  }

  function restoreAnonymous(anonymous) {
    if (!anonymous) return;
    writeJson(PROFILE_KEY, validProfile(anonymous.profile));
    writeJson(WATCHLIST_KEY, validWatchlist(anonymous.watchlist));
  }

  function updateAnonymous(changes) {
    if (isAuthenticated) return;
    const state = readState();
    const current = state.anonymous || snapshotCurrentAnonymous();
    state.anonymous = { ...current, ...changes };
    state.anonymous.fingerprint = anonymousFingerprint(state.anonymous);
    writeState(state);
  }

  function closeModal() {
    if (!activeModal) return;
    const { element, previousFocus, keyHandler } = activeModal;
    document.removeEventListener('keydown', keyHandler);
    document.documentElement.classList.remove('archive-ownership-open');
    element.remove();
    if (previousFocus?.isConnected) previousFocus.focus();
    activeModal = null;
  }

  function showModal(anonymous, pendingAction, onDecision) {
    const previousFocus = document.activeElement;
    const element = document.createElement('div');
    element.className = 'archive-ownership-modal';
    const watchlistContext = pendingAction?.action === 'save-watchlist';
    element.innerHTML = `
      <div class="archive-ownership-modal__backdrop" aria-hidden="true"></div>
      <section class="archive-ownership-modal__dialog" role="dialog" aria-modal="true"
        aria-labelledby="archiveOwnershipTitle" aria-describedby="archiveOwnershipDescription">
        <button type="button" class="archive-ownership-modal__close" aria-label="Close">&times;</button>
        ${watchlistContext ? '<p class="archive-ownership-modal__eyebrow">The Archive Watchlist</p>' : ''}
        <h2 id="archiveOwnershipTitle">${watchlistContext ? 'Keep your saved Watchlist?' : 'We found Archive data saved on this browser'}</h2>
        <p id="archiveOwnershipDescription">${watchlistContext ? 'We found Watchlist items saved in this browser. Would you like to add them to your Archive account?' : 'Profile selections or watchlist items were saved here while browsing as a guest. If this data is yours, you can keep it with your account.'}</p>
        <label class="archive-ownership-modal__trust">
          <input type="checkbox" data-archive-ownership-trust>
          <span>This is my private browser. Remember my choice for this account.</span>
        </label>
        <div class="archive-ownership-modal__actions">
          <button type="button" class="archive-ownership-modal__secondary" data-archive-ownership-decision="fresh">${watchlistContext ? 'Use Account Watchlist' : 'Start Fresh'}</button>
          <button type="button" class="archive-ownership-modal__primary" data-archive-ownership-decision="keep">${watchlistContext ? 'Keep Watchlist' : 'Keep &amp; Continue'}</button>
        </div>
      </section>
    `;

    const dialog = element.querySelector('.archive-ownership-modal__dialog');
    const controls = [...element.querySelectorAll('input, button')];
    const dismiss = () => onDecision('fresh', false, anonymous);
    const keyHandler = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    element.querySelectorAll('[data-archive-ownership-decision]').forEach(button => {
      button.addEventListener('click', () => {
        onDecision(
          button.dataset.archiveOwnershipDecision,
          element.querySelector('[data-archive-ownership-trust]').checked,
          anonymous
        );
      });
    });

    element.querySelector('.archive-ownership-modal__close').addEventListener('click', dismiss);
    element.querySelector('.archive-ownership-modal__backdrop').addEventListener('click', dismiss);

    document.addEventListener('keydown', keyHandler);
    document.documentElement.classList.add('archive-ownership-open');
    document.body.appendChild(element);
    activeModal = { element, previousFocus, keyHandler };
    dialog.querySelector('[data-archive-ownership-decision="keep"]').focus();
  }

  function resolution(decision, anonymous) {
    return {
      decision,
      profile: validProfile(anonymous?.profile),
      watchlist: validWatchlist(anonymous?.watchlist),
      fingerprint: anonymous?.fingerprint || null,
      pendingAction: null
    };
  }

  function initialise() {
    const state = readState();

    if (!isAuthenticated) {
      if (state.anonymous) {
        restoreAnonymous(state.anonymous);
      } else {
        state.anonymous = snapshotCurrentAnonymous();
        writeState(state);
      }
      finish(resolution('guest', state.anonymous));
      return;
    }

    const anonymous = state.anonymous;
    const pendingAction = pendingArchiveAction(anonymous);
    if (!meaningfulAnonymous(anonymous) || !pendingAction) {
      finish(resolution('fresh', anonymous));
      return;
    }

    const handoffResolution = decision => ({
      ...resolution(decision, anonymous),
      pendingAction
    });

    const existing = state.customers[customerId];
    if (existing?.fingerprint === anonymous.fingerprint) {
      if (existing.decision === 'fresh') clearPendingAction();
      finish(handoffResolution(existing.decision));
      return;
    }

    if (existing?.trusted === true && (existing.decision === 'keep' || existing.decision === 'fresh')) {
      state.customers[customerId] = { ...existing, fingerprint: anonymous.fingerprint };
      writeState(state);
      if (existing.decision === 'fresh') clearPendingAction();
      finish(handoffResolution(existing.decision));
      return;
    }

    const commitDecision = (decision, trusted) => {
      const latest = readState();
      const alreadyResolved = latest.customers[customerId];
      if (alreadyResolved?.fingerprint === anonymous.fingerprint) {
        closeModal();
        if (alreadyResolved.decision === 'fresh') clearPendingAction();
        finish(handoffResolution(alreadyResolved.decision));
        return;
      }
      latest.customers[customerId] = {
        decision,
        trusted,
        fingerprint: anonymous.fingerprint
      };
      writeState(latest);
      closeModal();
      if (decision === 'fresh') clearPendingAction();
      finish(handoffResolution(decision));
    };

    showModal(anonymous, pendingAction, commitDecision);

    window.addEventListener('storage', event => {
      if (event.key !== STATE_KEY) return;
      const latest = readState().customers[customerId];
      if (latest?.fingerprint !== anonymous.fingerprint) return;
      closeModal();
      if (latest.decision === 'fresh') clearPendingAction();
      finish(handoffResolution(latest.decision));
    });
  }

  window.ArchiveOwnership = {
    ready,
    profileHydrationReady,
    completeProfileHydration(result) {
      resolveProfileHydration(result);
    },
    createProfilePreservationIntent(returnPath) {
      return createPreservationIntent('save-profile', returnPath);
    },
    createWatchlistPreservationIntent(returnPath) {
      return createPreservationIntent('save-watchlist', returnPath);
    },
    consumePendingAction() {
      clearPendingAction();
    },
    captureAnonymousWatchlist(watchlist) {
      updateAnonymous({ watchlist: validWatchlist(watchlist) });
    },
    captureAnonymousProfile(profile) {
      updateAnonymous({ profile: validProfile(profile) });
    },
    markPersonaInteraction(profile) {
      updateAnonymous({ profile: validProfile(profile), personaInteracted: true });
    }
  };

  function createPreservationIntent(action, returnPath) {
      if (isAuthenticated) return null;
      const state = readState();
      const anonymous = state.anonymous || snapshotCurrentAnonymous();
      anonymous.fingerprint = anonymousFingerprint(anonymous);
      state.anonymous = anonymous;
      writeState(state);
      if (!meaningfulAnonymous(anonymous)) return null;
      if (action === 'save-watchlist' && validWatchlist(anonymous.watchlist).length === 0) return null;

      const nonce = window.crypto?.randomUUID?.() ||
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const pendingAction = {
        version: 1,
        action,
        returnPath,
        anonymousFingerprint: anonymous.fingerprint,
        nonce,
        createdAt: Date.now()
      };

      try {
        sessionStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(pendingAction));
      } catch (error) {
        console.warn(`Unable to write ${PENDING_ACTION_KEY}.`, error);
        return null;
      }

      return pendingAction;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
